/**
 * Tool types matching backend ga_kasko_eda_be.models (ToolType, ToolModel, UpdateTool).
 * Field names kept in snake_case for API compatibility.
 */

// biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
export type ToolType =
  | 'MfgDrillTool'
  | 'MfgSpotDrillTool'
  | 'MfgEndMillTool'
  | 'MfgThreadMillTool'
  | 'MfgTapTool'
  | 'MfgReamerTool'
  | 'MfgConicalMillTool';

/**
 * Machining purpose, mirroring backend `ToolPurpose`. Parsed from the hyperMILL name suffix,
 * so every tool has one; the backend defaults it to `base`.
 */
export type ToolPurpose = 'base' | 'roughing' | 'finishing';

/** All purpose values, in the order the in-cell dropdown offers them. */
export const TOOL_PURPOSE_VALUES: ToolPurpose[] = ['base', 'roughing', 'finishing'];

/** All tool type values for filter dropdowns. */
export const TOOL_TYPE_VALUES: ToolType[] = [
  'MfgDrillTool',
  'MfgSpotDrillTool',
  'MfgEndMillTool',
  'MfgThreadMillTool',
  'MfgTapTool',
  'MfgReamerTool',
  'MfgConicalMillTool',
];

export interface HolderModel {
  id: string;
  holder_name: string;
  diameter_mm_1: number;
  diameter_mm_2: number;
  len_mm_1: number;
  len_mm_2: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CreateHolder {
  holder_name?: string;
  diameter_mm_1: number;
  diameter_mm_2: number;
  len_mm_1: number;
  len_mm_2: number;
}

export interface UpdateHolder {
  holder_name?: string;
  diameter_mm_1?: number;
  diameter_mm_2?: number;
  len_mm_1?: number;
  len_mm_2?: number;
}

/**
 * A tool as the catalogue stores it.
 *
 * The holder and the NC number are *not* here: since the tool-database redesign both
 * belong to the assembly that mounts the tool (`AssemblyModel.holder` / `nc_number`),
 * because one tool can sit in several assemblies. Read them through `useToolAssemblies`.
 */
export interface ToolModel {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  tool_name: string;
  tool_type: ToolType;
  purpose: ToolPurpose;
  tool_comment: string;
  tool_diameter: number;
  /** Diameter of the cutting part (mm); null when the export did not state one. */
  cutting_diameter: number | null;
  tool_corner_rad: number | null;
  tool_cut_angle: number | null;
  tool_overall_len: number;
  tool_cut_len: number | null;
  tool_len: number;
  tool_rotation: string;
  /** Threaded length (mm); null when not applicable. */
  thread_length: number | null;
  horizontal_distance: number | null;
  vertical_distance: number | null;
  tip_angle: number | null;
  flank_angle: number | null;
  /** Ball-end tool flag. */
  ball_type: boolean;
  /** Retired tools stay in the catalogue with `active: false`. */
  active: boolean;
  /** Identity of the row in the hyperMILL export it was imported from. */
  obj_guid: string | null;
  /** Backend `ToolGeometry`; the UI passes it through without reading into it. */
  geometry_json?: unknown;
}

/** Payload for creating a new tool. All fields shown in the dialog (nullable = user can leave empty). */
export interface CreateTool {
  // Basic identification
  tool_name: string;
  tool_type: ToolType;
  tool_comment: string;
  // Core dimensions (required by backend)
  tool_diameter: number;
  tool_overall_len: number;
  tool_len: number;
  // Optional dimensions (nullable on backend)
  tool_corner_rad: number | null;
  tool_cut_angle: number | null;
  tool_cut_len: number | null;
  // Rotation
  tool_rotation: string;
  // Angle geometry (nullable on backend)
  horizontal_distance: number | null;
  vertical_distance: number | null;
  tip_angle: number | null;
  flank_angle: number | null;
  /** Backend declares a non-null `bool` with default `false`; omit the key to take the default. */
  ball_type?: boolean;
}

/** Update payload: only provided fields are sent to backend. */
export interface UpdateTool {
  tool_name?: string;
  tool_type?: ToolType;
  purpose?: ToolPurpose;
  tool_comment?: string;
  tool_diameter?: number;
  cutting_diameter?: number | null;
  tool_corner_rad?: number | null;
  tool_cut_angle?: number | null;
  tool_overall_len?: number;
  tool_cut_len?: number | null;
  tool_len?: number;
  tool_rotation?: string;
  thread_length?: number | null;
  horizontal_distance?: number | null;
  vertical_distance?: number | null;
  tip_angle?: number | null;
  flank_angle?: number | null;
  ball_type?: boolean | null;
  active?: boolean;
}
